<template>
    <div
        class="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
        v-if="paginator.total > 0"
    >
        <div class="flex-1 flex justify-between sm:hidden">
            <span
                v-if="paginator.prev_page_url === null"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-200 bg-white hover:bg-gray-50"
            >
                {{ $t('messages.previous') }}
            </span>
            <inertia-link
                v-else
                :href="paginator.prev_page_url"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
                {{ $t('messages.previous') }}
            </inertia-link>
            <span
                v-if="paginator.next_page_url === null"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-200 bg-white hover:bg-gray-50"
            >
                {{ $t('messages.next') }}
            </span>
            <inertia-link
                v-else
                :href="paginator.next_page_url"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
                {{ $t('messages.next') }}
            </inertia-link>
        </div>
        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
                <p
                    class="text-sm text-gray-700"
                    v-html="
                        $tc('messages.showing_from_X_to_Y_of_Z', 10, {
                            from: paginator.from,
                            to: paginator.to,
                            total: paginator.total,
                        })
                    "
                ></p>
            </div>
            <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <template v-for="(link, key) in paginator.links" :key="key">
                        <template v-if="link.label.startsWith('&laquo;')">
                            <inertia-link
                                v-if="link.url !== null"
                                :href="link.url"
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <span class="sr-only">{{ $t('messages.previous') }}</span>
                                <!-- Heroicon name: solid/chevron-left -->
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </inertia-link>
                            <span
                                v-else
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <svg
                                    class="h-5 w-5 text-gray-200"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </span>
                        </template>
                        <template v-else-if="link.label.endsWith('&raquo;')">
                            <inertia-link
                                v-if="link.url !== null"
                                :href="link.url"
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <span class="sr-only">{{ $t('messages.next') }}</span>
                                <!-- Heroicon name: solid/chevron-right -->
                                <svg
                                    class="h-5 w-5"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </inertia-link>
                            <span
                                v-else
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                            >
                                <svg
                                    class="h-5 w-5 text-gray-200"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                            </span>
                        </template>
                        <span
                            v-else-if="link.url === null && link.label === '...'"
                            class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                        >
                            ...
                        </span>
                        <inertia-link
                            preserve-scroll
                            v-else-if="link.url !== null"
                            :href="link.url"
                            :class="
                                link.active
                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            "
                            class="hidden md:inline-flex relative items-center px-4 py-2 border text-sm font-medium"
                        >
                            <span v-html="link.label" />
                        </inertia-link>
                    </template>
                </nav>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        paginator: Object,
    },
};
</script>
